import { 
  signIn, 
  signOut, 
  signUp, 
  confirmSignUp, 
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  AuthUser
} from 'aws-amplify/auth'

export interface AuthCredentials {
  username: string
  password: string
}

export interface SignUpCredentials extends AuthCredentials {
  email: string
}

export interface AuthSession {
  user: AuthUser | null
  tokens: {
    accessToken: string
    idToken: string
    refreshToken?: string
  } | null
  isAuthenticated: boolean
}

export interface AuthError {
  code: string
  message: string
}

class AuthService {
  private sessionCheckInterval: number | null = null
  private readonly SESSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

  /**
   * Sign in user with username and password
   */
  async signIn(credentials: AuthCredentials): Promise<AuthUser> {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: credentials.username,
        password: credentials.password
      })

      if (!isSignedIn) {
        throw new Error(`Sign in incomplete: ${nextStep.signInStep}`)
      }

      const user = await getCurrentUser()
      this.startSessionMonitoring()
      return user
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Sign up new user
   */
  async signUp(credentials: SignUpCredentials): Promise<{ userId?: string; nextStep: unknown }> {
    try {
      const { userId, nextStep } = await signUp({
        username: credentials.username,
        password: credentials.password,
        options: {
          userAttributes: {
            email: credentials.email
          }
        }
      })

      return { userId, nextStep }
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(username: string, confirmationCode: string): Promise<void> {
    try {
      await confirmSignUp({
        username,
        confirmationCode
      })
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Resend sign up confirmation code
   */
  async resendSignUpCode(username: string): Promise<void> {
    try {
      await resendSignUpCode({ username })
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut()
      this.stopSessionMonitoring()
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Sign out with redirect to Hosted UI
   * Use this when using Cognito Hosted UI for authentication
   */
  async signOutWithRedirect(): Promise<void> {
    try {
      const { signOut: amplifySignOut } = await import('aws-amplify/auth')
      await amplifySignOut()
      this.stopSessionMonitoring()
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Reset password
   */
  async resetPassword(username: string): Promise<void> {
    try {
      await resetPassword({ username })
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Confirm password reset with code
   */
  async confirmResetPassword(
    username: string, 
    confirmationCode: string, 
    newPassword: string
  ): Promise<void> {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword
      })
    } catch (error: unknown) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      return await getCurrentUser()
    } catch (error) {
      return null
    }
  }

  /**
   * Get current auth session with tokens
   */
  async getCurrentSession(): Promise<AuthSession> {
    try {
      console.log('[AuthService] getCurrentSession called')
      const user = await this.getCurrentUser()
      console.log('[AuthService] getCurrentUser result:', user)
      
      if (!user) {
        console.log('[AuthService] No user found, returning unauthenticated')
        return {
          user: null,
          tokens: null,
          isAuthenticated: false
        }
      }

      console.log('[AuthService] Fetching auth session...')
      const session = await fetchAuthSession()
      console.log('[AuthService] fetchAuthSession result:', {
        hasTokens: !!session.tokens,
        tokens: session.tokens ? {
          hasAccessToken: !!session.tokens.accessToken,
          hasIdToken: !!session.tokens.idToken
        } : null
      })
      
      return {
        user,
        tokens: session.tokens ? {
          accessToken: session.tokens.accessToken.toString(),
          idToken: session.tokens.idToken?.toString() ?? ''
        } : null,
        isAuthenticated: true
      }
    } catch (error) {
      console.error('[AuthService] Error in getCurrentSession:', error)
      return {
        user: null,
        tokens: null,
        isAuthenticated: false
      }
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await getCurrentUser()
      return !!user
    } catch (error) {
      return false
    }
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken.toString() || null
    } catch (error) {
      return null
    }
  }

  /**
   * Get user groups from Cognito token
   */
  async getUserGroups(): Promise<string[]> {
    try {
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken
      
      if (!idToken) {
        return []
      }

      // Cognito groups are stored in the 'cognito:groups' claim
      const payload = idToken.payload as { 'cognito:groups'?: string[] }
      return payload['cognito:groups'] || []
    } catch (error) {
      console.error('Error getting user groups:', error)
      return []
    }
  }

  /**
   * Check if user has any of the specified groups
   */
  async hasAnyGroup(groups: string[]): Promise<boolean> {
    try {
      const userGroups = await this.getUserGroups()
      return groups.some(group => userGroups.includes(group))
    } catch (error) {
      return false
    }
  }

  /**
   * Start monitoring session for token refresh
   */
  private startSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      window.clearInterval(this.sessionCheckInterval)
    }

    this.sessionCheckInterval = window.setInterval(async () => {
      try {
        await fetchAuthSession({ forceRefresh: true })
      } catch (error) {
        console.warn('Session refresh failed:', error)
        // Optionally trigger sign out or show notification
      }
    }, this.SESSION_CHECK_INTERVAL)
  }

  /**
   * Stop session monitoring
   */
  private stopSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      window.clearInterval(this.sessionCheckInterval)
      this.sessionCheckInterval = null
    }
  }

  /**
   * Handle and normalize auth errors
   */
  private handleAuthError(error: unknown): AuthError {
    const errorCode = (error as { name?: string; code?: string }).name || 
                     (error as { name?: string; code?: string }).code || 'UnknownError'
    const errorMessage = (error as { message?: string }).message || 'An unknown error occurred'

    // Map common Cognito errors to user-friendly messages
    const errorMap: Record<string, string> = {
      'UserNotFoundException': 'User not found. Please check your username.',
      'NotAuthorizedException': 'Incorrect username or password.',
      'UserNotConfirmedException': 'Please confirm your account before signing in.',
      'PasswordResetRequiredException': 'Password reset is required.',
      'UserAlreadyExistsException': 'An account with this username already exists.',
      'InvalidPasswordException': 'Password does not meet requirements.',
      'CodeMismatchException': 'Invalid verification code.',
      'ExpiredCodeException': 'Verification code has expired.',
      'LimitExceededException': 'Too many attempts. Please try again later.',
      'TooManyRequestsException': 'Too many requests. Please try again later.'
    }

    return {
      code: errorCode,
      message: errorMap[errorCode] || errorMessage
    }
  }
}

// Export singleton instance
export const authService = new AuthService()
export default authService